import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LastClean } from '@shared/schemas';

import type {
  BrokenUrlProfile,
  DuplicateGroup,
  ExitsInvestmentsToCompleteProfile,
  MixedCurrencyExitProfile,
} from '../domain/profile-cleaning.model';
import { emptyNotice, initialState, type Notice } from './profile-cleaning.state';

/** Payload for storing the lists together. */
export interface CleaningData {
  brokenUrlProfiles: BrokenUrlProfile[];
  duplicateGroups: DuplicateGroup[];
  /** Read only (#709); optional so a refresh that predates it leaves it as it was. */
  mixedCurrencyProfiles?: MixedCurrencyExitProfile[];
  /** Read only (#754); optional for the same reason. */
  toCompleteProfiles?: ExitsInvestmentsToCompleteProfile[];
  /** The profiles to complete could not be read this time (#754). */
  toCompleteFailed?: boolean;
}

/**
 * Admin Profile Cleaning slice. Reducers are pure state transitions only; the
 * initial load, the clean orchestrations and the inline group merge all
 * live in the saga, which dispatches these transitions as it progresses.
 * `dataSet` is the silent refresh used after an operation (no loading spinner);
 * `loadStarted`/`loadSucceeded`/`loadFailed` drive the first mount's spinner.
 */
const slice = createSlice({
  name: 'profileCleaning',
  initialState,
  reducers: {
    // ── Initial load lifecycle ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<CleaningData>) => {
      state.brokenUrlProfiles = action.payload.brokenUrlProfiles;
      state.duplicateGroups = action.payload.duplicateGroups;
      state.mixedCurrencyProfiles =
        action.payload.mixedCurrencyProfiles ?? state.mixedCurrencyProfiles;
      state.toCompleteProfiles = action.payload.toCompleteProfiles ?? state.toCompleteProfiles;
      state.toCompleteFailed = action.payload.toCompleteFailed ?? state.toCompleteFailed;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    /** Silent refresh of both lists (after a clean / merge). */
    dataSet: (state, action: PayloadAction<CleaningData>) => {
      state.brokenUrlProfiles = action.payload.brokenUrlProfiles;
      state.duplicateGroups = action.payload.duplicateGroups;
      state.mixedCurrencyProfiles =
        action.payload.mixedCurrencyProfiles ?? state.mixedCurrencyProfiles;
      state.toCompleteProfiles = action.payload.toCompleteProfiles ?? state.toCompleteProfiles;
      state.toCompleteFailed = action.payload.toCompleteFailed ?? state.toCompleteFailed;
    },

    /** The latest clean, read on mount and again after every clean / merge. */
    lastCleanSet: (state, action: PayloadAction<LastClean | null>) => {
      state.lastClean = action.payload;
      state.lastCleanLoaded = true;
    },

    // ── Busy flags ──
    cleanStarted: (state) => {
      state.cleaning = true;
    },
    cleanStopped: (state) => {
      state.cleaning = false;
    },
    mergeStarted: (state, action: PayloadAction<string>) => {
      state.mergingGroupId = action.payload;
    },
    mergeStopped: (state) => {
      state.mergingGroupId = null;
    },
    // ── "Convert currencies" dialog (#732) ──
    convertDialogOpened: (state, action: PayloadAction<string>) => {
      state.convertProfileId = action.payload;
    },
    convertDialogClosed: (state) => {
      state.convertProfileId = null;
    },
    convertStarted: (state) => {
      state.converting = true;
    },
    convertStopped: (state) => {
      state.converting = false;
    },
    /** A scoped clean settled; the page and the table drop the ticked rows. */
    selectionResetRequested: (state) => {
      state.selectionResetToken += 1;
    },

    /** The sidebar badge's count (#753); a failed read leaves the last one. */
    issueCountSucceeded: (state, action: PayloadAction<number>) => {
      state.issueCount = action.payload;
    },

    // ── Operation notice (snackbars) ──
    noticeSet: (state, action: PayloadAction<Notice>) => {
      state.notice = action.payload;
    },
    noticeCleared: (state) => {
      state.notice = emptyNotice;
    },
  },
});

export const profileCleaningActions = slice.actions;
export const profileCleaningReducer = slice.reducer;
